// @spec GHG-STR-016
import { createFileRoute } from '@tanstack/react-router';
import { AlertCircle, CheckCircle, Clock, XCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { usePublicSurvey } from '@/features/ghg/commuting-surveys/api';
import { PublicSurveyForm } from '@/features/ghg/commuting-surveys/components';

export const Route = createFileRoute('/ghg-survey/$token')({
  component: PublicSurveyPage,
});

/**
 * Loading skeleton for the survey page.
 */
function SurveyLoadingSkeleton() {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="space-y-6">
          {/* Header skeleton */}
          <div className="space-y-2 text-center">
            <Skeleton className="mx-auto h-8 w-64" />
            <Skeleton className="mx-auto h-4 w-40" />
          </div>

          {/* Card skeleton */}
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent className="space-y-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

interface ErrorStateProps {
  title: string;
  message: string;
  icon: React.ReactNode;
}

/**
 * Error state component.
 */
function ErrorState({ title, message, icon }: ErrorStateProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
            {icon}
          </div>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">{message}</p>
          <Button
            variant="outline"
            className="mt-6"
            onClick={() => {
              window.location.reload();
            }}
          >
            Try Again
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Public commuting survey page.
 *
 * This is a PUBLIC page that allows employees to complete commuting surveys
 * using a magic link token. No authentication required.
 */
function PublicSurveyPage() {
  const { token } = Route.useParams();
  const { data, isLoading, isError, error } = usePublicSurvey(token);

  // Loading state
  if (isLoading) {
    return <SurveyLoadingSkeleton />;
  }

  // Error states
  if (isError || !data) {
    const typedError = error as (Error & { code?: string }) | null;
    const errorCode = typedError?.code;

    if (errorCode === 'TOKEN_EXPIRED' || errorCode === 'SURVEY_EXPIRED') {
      return (
        <ErrorState
          title="Survey Expired"
          message="This survey link has expired. Please contact your organization for a new link."
          icon={<Clock className="h-8 w-8 text-muted-foreground" />}
        />
      );
    }

    if (errorCode === 'ALREADY_RESPONDED') {
      return (
        <ErrorState
          title="Already Submitted"
          message="You have already submitted a response to this survey. Thank you for participating!"
          icon={<CheckCircle className="h-8 w-8 text-green-600" />}
        />
      );
    }

    if (errorCode === 'SURVEY_CLOSED') {
      return (
        <ErrorState
          title="Survey Closed"
          message="This survey is no longer accepting responses. Thank you for your interest."
          icon={<XCircle className="h-8 w-8 text-muted-foreground" />}
        />
      );
    }

    return (
      <ErrorState
        title="Invalid Link"
        message="This survey link is invalid. Please check the link and try again, or contact your organization."
        icon={<AlertCircle className="h-8 w-8 text-muted-foreground" />}
      />
    );
  }

  // Render survey form
  return <PublicSurveyForm token={token} survey={data} />;
}
